/**
 * Component-validator registry (CZNA-371, ADR-0005 D8 / ADR-0006 D4).
 *
 * <p>`ComponentDef.componentValidator` names a validator; this file owns the
 * implementations. It was previously a closed TypeScript literal
 * (`componentValidator?: 'yearQualifier'`) matched by a hardcoded `if` in
 * `DynamicFieldPanel`, so a second validator meant touching the renderer.
 *
 * <p>Validators return a normalised `{ level, messageKey }` rather than each
 * one's own result shape, so the renderer maps error vs warning once instead of
 * per validator. `messageKey` is the unprefixed i18n key — Stripes prepends
 * `ui-czna-authorities.` at runtime.
 *
 * <p>An unknown name yields `null` (no validation) plus one warning, matching
 * the tolerant-loading rule in `codecRegistry.ts`: a config that names a
 * validator this build lacks must still open.
 */
import { validateYearQualifier } from '../utils/yearQualifier';
/** Validator whitelist. Keys are the names a form config may put in `componentValidator`. */
export var COMPONENT_VALIDATORS = {
    /**
     * TASK-092 tolerant year/period validator — accepts the NKP formats
     * (`YYYY`, `YYYY-YYYY`, `YYYY-`, `-YYYY`, `YYYY?`, `NN. léta`, …) and
     * distinguishes a hard error (invalid / inverted range) from a warning
     * (unrecognised but not provably wrong).
     */
    yearQualifier: function (value) {
        var result = validateYearQualifier(value);
        if (result.valid === false) {
            return { level: 'error', messageKey: result.message };
        }
        if ('level' in result && result.level === 'warning') {
            return { level: 'warning', messageKey: result.message };
        }
        return null;
    },
};
/** One warning per unknown name — the lookup runs from the render path. */
var warnedNames = new Set();
/** Test seam: the warn-once set is module state and would leak across cases. */
export var __resetValidatorWarnings = function () { return warnedNames.clear(); };
/** Resolves a validator name against the whitelist; `null` (with one warning) when unknown. */
export var lookupComponentValidator = function (name) {
    if (!name)
        return null;
    var validator = COMPONENT_VALIDATORS[name];
    if (!validator) {
        if (!warnedNames.has(name)) {
            warnedNames.add(name);
            // eslint-disable-next-line no-console
            console.warn("[componentValidatorRegistry] form config references unknown componentValidator \"".concat(name, "\" \u2014 component is not validated"));
        }
        return null;
    }
    return validator;
};
